import { Component } from '@angular/core';
import { DataLayerGeojsonPointComponent } from './data-layer-geojson-point.component';
import { MapService } from './map.service';
import { DataLayerService } from './data-layer.service';
import { ConfigService } from './config.service';
import { UserMessageService } from './user-message.service';

declare var L;

/*
 * data-layer-geojson-point-dws defines a class to display data from data web services.  This filters on data type (SHEF).
 * The data format is geojson.
 *
 * --------------------------------
 *
 * Within the configuration file, data web services stations can be included:
 * "overlayLayers": [
 * 		{"id": "batteryVoltage",
 * 			"label": "Battery Voltage",
 * 			"url": "stationSummaries.geojson,
 * 			"dataType": VoltageBattery,
 * 			"statisticType": "Max",
 * 			"duration": "1day",
 * 			"title": "statistics.statisticValue",
 *		 	"class": "data-layer-geojson-point-dws",
 			"refresh": true },
 * ...
 * ]
 */

@Component({
	selector: 'data-layer-geojson-point-dws',
	standalone: false,
	template: `
		<div class="checkboxDiv"><label class="overlay-layer">
		<input type="checkbox" (click)="menuControlSelected($event)">{{htmlElementLabel}}</label>
		<app-single-legend [active]=active [legendUrl]=legendUrl></app-single-legend>
		</div>
		`,
	styleUrls: ['app.component.css']
})
export class DataLayerGeojsonPointDWSComponent extends DataLayerGeojsonPointComponent {

	dataType = 'FRWS';
	statisticType = 'Last';
	duration = '1day';
	title = 'station.id';

	novaScores: any[];
	colorValueTable: any = null;
	alarmTriggers: any[];

	// tooltips contain data for different periods of record
	// the following array defines those tables
	tooltipTablePORPrecip = ['15Minute', '30Minute', '1Hour', '3Hour', '6Hour', '12Hour', 
		'1Day', '3Day', '7Day', '30Day'];
	tooltipTablePOROther = ['15Minute', '1Hour', '3Hour', '1Day', '7Day'];


	constructor (
		mapService: MapService,
		dataLayerService: DataLayerService,
		configService: ConfigService,
		userMessageService: UserMessageService ) {

		super (mapService, dataLayerService, configService, userMessageService );
		this.htmlElementLabel = 'DWS';
	}

	setData ( someData: any ) {
		// Set the standard data
		super.setData(someData);

		// Set data unique to DWS
		if ( someData.hasOwnProperty('dataType')) {
			this.dataType = someData.dataType;
		}
		if ( someData.hasOwnProperty('statisticType')) {
			this.statisticType = someData.statisticType;
		}
		if ( someData.hasOwnProperty('duration')) {
			this.duration = someData.duration;
		}
		if ( someData.hasOwnProperty('title')) {
			this.title = someData.title;
		}
		if ( someData.hasOwnProperty('colorValueTable')) {
			this.colorValueTable = someData.colorValueTable;
		}
	}

	onEachPointFeature(feature, layer) {
		// console.log(feature);
		const stationName = 'name';
		let popupContent, tooltipContent;
		// const options = { minWidth: 320 };  For example
		const options = { };
		var tooltipOptions = {"offset": [100,100]};

		popupContent = '<p style="font-size:20px">' +
			'Station name: ' + feature.properties.stationSummary[stationName] +
			"</p> <button class=\"popupButton\" onclick='globalMapComponent.gotoDetail(" +
			feature.properties.stationSummary.numId +
			")'>Go to Station Dashboard</button>";

		// next, assign tool tip content
		tooltipContent = feature.properties.stationSummary.tooltipContent;

		layer.bindPopup(popupContent, options);
		layer.bindTooltip(tooltipContent, tooltipOptions);
	}

	// map.service placeMarkers is being used, not this anymore
	placeMarkers = (feature, latlng) => {

		// Determine the color and text color of the marker based on the novascore.
		const self = this;
		console.log("data-layer-geojson-point-dws.placeMarkers");
		const dwsNS = feature.properties.stationSummary.dwsNovaScore;
		let color, textColor, markerType = "marker";

		const value = feature.properties.stationSummary.dwsTitle;
		const table = feature.properties.stationSummary?.colorValueTable;
		console.log(value, table );
		if ( table != null ) {
			let tableEntry = this.mapService.convertValueToTable( value, table );
			color = tableEntry == null ? "black" : tableEntry[2];
			textColor = tableEntry == null ? "white" : tableEntry[3];
			markerType = tableEntry == null ? "marker" : tableEntry[4];
		} else {
			color = this.mapService.convertScoreToColor( dwsNS );
			textColor = this.mapService.convertScoreToTextColor( dwsNS );
		}

		let title;
		let marker;
		const alarms = feature.properties.stationSummary['alarms'];
		let alarmClasses = '';

		/* Indicate if this station is in an alarm status */
		if ( alarms.length > 0 ) {
			alarms.forEach ( alarm => {
				if ( alarm.alarmState === 'Active' ) {
					alarmClasses = 'alarm-icon blinking';
				}
			});
		}

		if ( value === null || value === 'NR' || value === 'OOS') {
			if ( value === 'OOS' ) {
				marker = L.icon({
					iconUrl: 'assets/images/icon-png-1circle-blue.png',
					iconSize: [20, 20],
					iconAnchor: [10, 10]
				});
			} else {
				marker = L.icon({
					iconUrl: 'assets/images/icon-png-1circle-black.png',
					iconSize: [20, 20],
					iconAnchor: [10, 10]
				});
			}
			// marker.options.shadowSize = [50, 75]; // ?
		} else {
			title = value;
			let titleForWidth = value;
			// for when we are ready to display indicators in the markers
			/* complete change when Steve has change completed
			if ( change != undefined ) {
				title =  change + " " + value;
				// title = "&#8657 &#8660 " + value + " &#8607" + "&#8648";
				titleForWidth = "X " + value;
			}
			*/
			
			if ( markerType == "marker") {
				marker = this.mapService.makeMarker( title, titleForWidth, color, textColor, alarmClasses);
			} else if ( markerType == "circle") {
				marker = this.mapService.makeMarkerCircle( color, alarmClasses);
			/* old way.  now we make custom markers 
			marker = L.ExtraMarkers.icon({
				extraClasses: alarmClasses,
				icon: 'fa-number',
				iconColor: textColor,		// text color
				markerColor: color,		// marker background color
				shape: 'square',
				prefix: 'fa',
				number: title
			});
			*/
			}
			// marker = this.makeMarker( title, color, textColor, alarmClasses );
			marker.options.shadowSize = [50, 75];
		}

		return L.marker(latlng, {icon: marker});  // http://stackoverflow.com/questions/29080194/leaflet-adding-an-offset-to-marker
	}

	// Parses the stationSummaries.geojson file, filters the features so only those containing specified dataType
	// such as "Battery Voltage" are included and stores the results in this.value.
	// Information is added to this layer so station has a title and a NovaScore at the main level.
	// This is used in the map display.
	parseDataFile () {
		const self = this;
		let unitsString = '';

		return new Promise(function(resolve, reject) {

			const request = new XMLHttpRequest();
			request.onload = function ( ) {
				if ( request.readyState === request.DONE ) {
					if ( request.status === 200 ) {
						// first, get geojson file content
						const dwsStations = JSON.parse(request.responseText);

						// filter the features to only include those containing specified dataTypes
						dwsStations.features = dwsStations.features.filter(function(s) {
							const dt = s.properties.stationSummary.dataTypes;
							const dt_filtered = dt.filter(function (d) { return d.name === self.dataType; });
							return dt_filtered.length === 0 ? false : true; });

						// If station is out of service, set dwsTitle to "OOS" and novaScore to -1
						// If station has no report, set dwsTitle to "NR" and novaScore to 0

						// first set the novaScores array
						self.novaScores = self.parseAndSetNovaScores( dwsStations );
						self.alarmTriggers = self.parseAndSetAlarmTriggers (dwsStations);

						dwsStations.features.forEach(function(item, index) {
							if ( item.properties.stationSummary.outOfService === true ) {
									item.properties.stationSummary.dwsTitle = 'OOS';
									item.properties.stationSummary.dwsNovaScore = -1;
									item.properties.stationSummary.tooltipContent =
										'<span class=\'tooltipHeader\'>' + item.properties.stationSummary.name + '</span>' +
										'<br>Out of service';
									// console.log("Setting waterLevelRiverNovaScore to -1");
							} else {
								// If desired title is part of the station info, set it now.
								if ( self.title.startsWith('station')) {
									const parts = self.title.split('.');
									if ( parts.length === 3 ) {
										// Something like station.id.0 meaning stationSummary.id rounded to 0 decimals
										item.properties.stationSummary.dwsTitle = item.properties.stationSummary[parts[1]].toFixed(Number(parts[2])); // .toFixed());
									} else if ( parts.length === 2 ) {
										// Something like station.name meaning stationSummary.name
										item.properties.stationSummary.dwsTitle = item.properties.stationSummary[parts[1]];
									}
								}

								const tmpStatistic = item.properties.stationSummary.statistics.filter(function(stat) {
									return stat.dataType.toUpperCase() === self.dataType.toUpperCase() &&
										stat.statisticType.toUpperCase() === self.statisticType.toUpperCase() &&
										stat.duration.toUpperCase() === self.duration.toUpperCase();
								});
								if ( tmpStatistic.length > 0 && tmpStatistic[0].statisticValue != null) {
										const tmpS = tmpStatistic[0];
										if ( unitsString === '' ) {
											unitsString = tmpS.statisticUnits;
										}

										// console.log(tmpStatistic[0].novaScore);
										const novaScore = self.convertNovaScore( self.novaScores, tmpS.novaScore );
										item.properties.stationSummary.dwsNovaScore = novaScore;
										item.properties.stationSummary.colorValueTable = self.colorValueTable;

										const alarmsMessage = self.createAlarmMessage ( item.properties.stationSummary['alarms'] );

										// If desired title is part of the station info, set it now.
										if ( self.title.startsWith('statistics')) {
											const parts = self.title.split('.');
											if ( parts.length === 3 ) {
												// Something like statistics.statisticValue.0 meaning stationSummary.statistics.statisticValue rounded to 0 decimals
												item.properties.stationSummary.dwsTitle = tmpS[parts[1]].toFixed(Number(parts[2])); // .toFixed());
											} else if ( parts.length === 2 ) {
												item.properties.stationSummary.dwsTitle = tmpS[parts[1]];
											}
										}
										item.properties.stationSummary.tooltipContent =
											self.createTooltipContent ( 
												dwsStations.features,
												item.properties.stationSummary.latitude,
												item.properties.stationSummary.longitude, [ self.dataType], 'Last', '1day', null );
									} else { // no report but still in service
										item.properties.stationSummary.dwsTitle = 'NR';
										item.properties.stationSummary.dwsNovaScore = 0;
										item.properties.stationSummary.tooltipContent =
											'<span class=\'tooltipHeader\'>' + item.properties.stationSummary.name + '</span><br>' +
											'id: ' + item.properties.stationSummary.numId + '<br>' +
											'No report';
									}
							}
						});
						// console.log(dwsStations);
						self.value = dwsStations;
						self.valueObservable.next(dwsStations);
						console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' +
							self.url + ' for all ' + self.id + ' stations was successfully completed.');

						resolve(true);
					} else {	// request status wasn't 200
						console.log ( '[' + new Date().toLocaleTimeString() + '] Reading ' + uri + ' for DWS was UNSUCCESSFUL.');
						reject(Error(request.statusText));
					}

				}
			};

			const uri = self.configService.createURI ( { 'filename': self.url });
			request.open('get', uri, true );
			console.log('[' + new Date().toLocaleTimeString() + '] Attempting to read ' + uri + ' for DWS data.');
			request.send();
		});

	}

	// set the novaScores variable to the novaScores array in the summaries file
	// pass in file json object representing the summaries file.
	parseAndSetNovaScores (stationSummaries) {
		let novaScores;
		for ( let i = 0; i < stationSummaries.properties.length; i++ ) {
			if ( Object.keys(stationSummaries.properties[i] === 'novaScores' )) {
				novaScores = stationSummaries.properties[i].novaScores;
				break;
			}
		}
		// console.log(this.novaScores);
		return novaScores;
	}

	// Convert a novascore from novascore to groupScoreOrder.
	//
	convertNovaScore ( novaScores: any[], novascore ) {  // may need to add scoreGroup
		const nsArray = novaScores.filter(function(stat) {
			return stat.id === novascore; });

		if ( nsArray.length > 0 ) {
			return nsArray[0].groupScoreOrder;
		} else { return 1; }
	}

	// Given a groupScoreOrder, return the description.
	//
	convertGroupNovaScoreToDescription ( novaScores: any[], groupScoreOrder ) {  // may need to add scoreGroup
		const nsArray = novaScores.filter(function(stat) {
			return stat.groupScoreOrder === groupScoreOrder; });

		if ( nsArray.length > 0 ) {
			return nsArray[0].description;
		} else { return ''; }
	}


	// set the alarm triggers variable to the alarm triggers array in the summaries file
	// pass in file json object representing the summaries file.
	parseAndSetAlarmTriggers (stationSummaries) {
		let alarmTriggers;
		for ( let i = 0; i < stationSummaries.properties.length; i++ ) {
			if ( stationSummaries.properties[i].hasOwnProperty ('alarmTriggers')) {
				alarmTriggers = stationSummaries.properties[i].alarmTriggers;
				break;
			}
		}
		// console.log(this.alarmTriggers);
		return alarmTriggers;
	}

	// Create a message to indicate if this station is in an alarm status.
	createAlarmMessage ( alarms ) {
		let alarmMessage = '';
		try {
			if ( alarms.length > 0 ) {
				for ( let i = 0; i < alarms.length; i++ ) {
					// get the alarm name
					if ( alarms[i].alarmState === 'Active' ) {

					let alarmName = '';
					let novaScoreClass = '';
					let novaScore = 1;
					const alarmsArray = this.alarmTriggers.filter(function(stat) {
						return stat.id === alarms[i].triggerId; });
					if ( alarmsArray.length > 0 ) {
						alarmName = alarmsArray[0].name;
						novaScore = alarmsArray[0].novaScore;
						novaScoreClass = 'class = "novascore' + alarmsArray[0].novaScore + '"';
					}
					// create the alarm hover message
					const alarmDateTime = 
						this.dateToISOButLocal(alarms[i].alarmDateTime);
					alarmMessage += 
						'<tr><td>' + alarmDateTime +
						'</td><td>' + alarmName +
						'</td><td ' + novaScoreClass + '>' + novaScore +
						'</td><td>' + alarms[i].triggerValue + ' ' + alarms[i].triggerValueUnits +
						'</td><td>' + alarms[i].triggerMessage + '</td></tr>';
					}
				}
				if ( alarmMessage.length > 0 ) {
					const alarmHeader = '<tr>' +
						'<th>Date/time</th>' +
						'<th>Alarm name</th>' +
						'<th>NovaScore</th>' +
						'<th>Trigger value</th>' +
						'<th>Message</th></tr>';
					alarmMessage = '<br>Active alarms:<table class="tt">'  + 
						alarmHeader + alarmMessage + '</table>';
				}
			}
		} catch (err) {
			console.log('Error processing alarm.');
		}
		return alarmMessage;
	}

	// Create the content when the user hovers over a station.
	// stats are the statistic filtered by the current data type
	// return the last statistic that has a value in order to understand
	// dates for the tooltip
	getLastReportStatistic ( stats, statisticType ) {
		let lastReport = null;

		if ( stats == null || stats.length == 0 ) {
			return lastReport;
		}
		const tStat = stats.filter(function(stat) {
			return stat.statisticType === statisticType;
		});

		for ( let i = 0; i < tStat.length; i++ ) {
			if ( tStat[i].statisticDateTime != null ) {
				lastReport = tStat[i];
				// end loop
				i = tStat.length;
			}
		}
		return lastReport;
	}
	//
	// This function creates only the table for the tooltip
	createTooltipTable ( dataTypeStatistics, statisticType, columns ) {
		let statisticsSummary = '<table class="tt">';
		
		statisticsSummary += this.createTooltipTableHeader (columns);

		for ( let i = 0; i < statisticType.length; i++ ) {
			const tmpStatistics = dataTypeStatistics.filter(function(stat) {
				return stat.statisticType === statisticType[i];
			});
			statisticsSummary += this.createTooltipTableRow ( 
				tmpStatistics, statisticType[i], columns );
		}
		statisticsSummary += '</table>';
		return statisticsSummary;
	}

	getDurationString ( duration ) {
		switch ( duration ) { 
			case '15Minute': return '15 Minutes'; break;
			case '30Minute': return '30 Minutes'; break;
			case '1Hour': return '1 Hour'; break;
			case '3Hour': return '3 Hours'; break;
			case '6Hour': return '6 Hours'; break;
			case '12Hour': return '12 Hours'; break;
			case '1Day': return '1 Day'; break;
			case '3Day': return '3 Days'; break;
			case '7Day': return '7 Days'; break;
			case '30Day': return '30 Days'; break;
			default: return 'Undefined';
		}
	}

	createTooltipTableHeader ( columns ) {
		let statisticsHeader = '<tr>';
		statisticsHeader += '<th>Statistic</th>';
		for ( let i = 0; i < columns.length; i++ ) {
			const duration = columns[i];
			statisticsHeader += '<th>' + this.getDurationString(duration) + '</th>';
		}
		statisticsHeader += '</tr>';
		return statisticsHeader;
	}

	createTooltipTableRow ( dataTypeStatistics, statisticType, columns ) {
		let statisticsStats = '<tr>';
		let first = true;

		// create the statistics summary which includes all durations
		for ( let i = 0; i < columns.length; i++ ) {
			const duration = columns[i];
			const s = dataTypeStatistics.filter(function(stat) {
				return stat.duration === duration;
			});
			if ( first == true ) {  // set units in table
				let units = dataTypeStatistics.length > 0 ? 
					dataTypeStatistics[0].statisticUnits: 'in';
				units = units.length > 0 ? (' ('+units+')') : '';

				const dataType = dataTypeStatistics.length > 0 ?
					' ' + dataTypeStatistics[0].dataType + ' ' : '';
				statisticsStats += '<td>' + dataType + statisticType + units + '</td>';
				first = false;
			}

			if ( s.length > 0 && s[0].statisticValue != null ) {
				if ( this.tooltipNSColorOnly ) {
					let novaScoreClass = 'class = "novascore' + s[0].novaScore + '"';
					statisticsStats += '<td ' + novaScoreClass + '></td>';
				} else {
					statisticsStats += '<td>' + s[0].statisticValue.toFixed(2) + '</td>';
				}

				// special circumstances for statistic type of "Last"
				// only 1 value shall be displayed
				if ( statisticType === 'Last') {
					while ( ++i < columns.length ) {
						statisticsStats += '<td class="nullCell">' + '</td>';
					}
				}
			}
			else {
				statisticsStats += '<td class="nullCell">' + '</td>';
			}
		}
		statisticsStats += '</tr>';

		return ( statisticsStats);
	}

	// This comprehensive function is for precip, stage, and flow stations.
	// interval is only used for precip.
	createTooltipContent ( features, latitude, longitude, dataTypeArray, statisticType, duration, interval ) {
		// console.log('In createTooltipContent');
		const self = this;
		let tooltipContent = '';
		// precip has several more steps involved so isPrecip is a more readable/highly used boolean
		const isPrecip = dataTypeArray.includes('Precip') ? true : false;

		// sift through every feature to find all that match the latitude and longitude passed in the parameter list
		features.forEach(function(feature, index) {
			if ( feature.properties.stationSummary.latitude === latitude && feature.properties.stationSummary.longitude === longitude ) {
				// first the name and id regardless of the status
				tooltipContent += 
							'<span class=\'tooltipHeader\'>' + feature.properties.stationSummary.name + '</span><br>' +
							'id: ' + feature.properties.stationSummary.numId + '<br>';

				// 3 possible status outcomes:
				// 		1) out of service
				// 		2) last statistical value(s)
				// 		3) in service but no report
				if ( feature.properties.stationSummary.outOfService === true )	{
					tooltipContent += 'Out of service<br><br>';
				} else {
					// tmpStatistics are the statistics for all durations
					// tmpStatistic is the statistic for the specified duration
					const tmpStatistics = feature.properties.stationSummary.statistics.filter(function(stat) {
						return dataTypeArray.includes(stat.dataType);
					});
					const tmpStatistic = tmpStatistics.filter(function(stat) {
						return stat.duration.toUpperCase() === duration.toUpperCase() &&
							stat.statisticType === statisticType;
					});

					const statisticTypes: any[] = [];
					let lastReportStat: any = null;
					let columns;
					if ( isPrecip ) {
						statisticTypes.push ( 'Total');
						columns = self.tooltipTablePORPrecip;
						const sss = feature.properties.stationSummary.statistics.filter(function(stat) {
						return (stat.dataType === 'PrecipAccum');
							});
						// Precip Total is calculated rather than instantaneous
						// Use PrecipAccum's last report date/time
						lastReportStat = self.getLastReportStatistic (
							sss, 'Last' );
					} else {
						statisticTypes.push ( 'Last', 'Max', 'Min');
						columns = self.tooltipTablePOROther;
						lastReportStat = self.getLastReportStatistic (
							tmpStatistics, statisticTypes[0] );
					}

					// add last report date
					if ( lastReportStat != null ) {
						tooltipContent += 'Time of last report: ' + 
							self.dateToISOButLocal(
							lastReportStat.statisticDateTime) + '<br>';
					}
					
					let statisticsSummary = self.createTooltipTable ( 
						tmpStatistics, statisticTypes, columns );

					// If a stastistic was returned for the current datatype, stastistic type, and duration
					if ( tmpStatistic.length > 0 && tmpStatistic[0].statisticValue != null) {
						// console.log(tmpStatistic[0].novaScore);
						const novaScore = self.convertNovaScore( 
							self.novaScores, tmpStatistic[0].novaScore );
						const alarmsMessage = self.createAlarmMessage ( 
							feature.properties.stationSummary['alarms']);

						// add report created date
						const computeDateString = lastReportStat != null ? 
							'Current time used to compute statistics: ' + 
							self.dateToISOButLocal( lastReportStat.periodEnd) + '<br>' : '';

						tooltipContent +=
							'<span class=\'tooltipContent\'>' +
							statisticsSummary +
							computeDateString +
							'Conditions: ' + self.convertGroupNovaScoreToDescription(
								self.novaScores, novaScore) +
							alarmsMessage +
							'<br>Click marker for more information</span><br>';
					} else { // no report but still in service
						// precipitation no report
						// If station truly has no reports, then tooltipContent should just say "No report".
						// But if there are any reports, display all reports.
						if ( isPrecip ) {
							let statisticsSummaryStringNR = '';
							if ( statisticsSummary.length !== 0 ) {
								statisticsSummaryStringNR = ' in ' + interval + statisticsSummary;
							}
							tooltipContent += 'No report' + statisticsSummaryStringNR;
						// all other data types no report
						} else {
							tooltipContent += 'No report<br><br>';
						}
					}
				}
			}
		});
		return tooltipContent;
	}
}
